import React, { useRef, useCallback } from 'react';
import { hexToHsl, hslToHex } from './colorUtils';

const SIZE = 320;
const OUTER = SIZE / 2;
const INNER = OUTER * 0.42;
const CENTER = SIZE / 2;

function polarToCart(angleDeg, radius) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: CENTER + radius * Math.cos(rad),
    y: CENTER + radius * Math.sin(rad),
  };
}

function describeSegment(startAngle, endAngle, outerR, innerR) {
  const start = polarToCart(startAngle, outerR);
  const end = polarToCart(endAngle, outerR);
  const innerStart = polarToCart(startAngle, innerR);
  const innerEnd = polarToCart(endAngle, innerR);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return [
    `M ${start.x} ${start.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${end.x} ${end.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

const SEGMENTS = 72; // 5° each
const SEG_ANGLE = 360 / SEGMENTS;

export default function ColourWheel({ selectedHex, onSelect, highlightedHues }) {
  const svgRef = useRef(null);
  const { h: selH } = hexToHsl(selectedHex);

  const handleClick = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = e.clientX - rect.left - CENTER;
    const y = e.clientY - rect.top - CENTER;
    const dist = Math.sqrt(x * x + y * y);
    if (dist < INNER || dist > OUTER) return;
    let angle = (Math.atan2(y, x) * 180) / Math.PI + 90;
    if (angle < 0) angle += 360;
    const hue = Math.round(angle) % 360;
    // t=0 at inner edge (dark), t=1 at outer edge (light) — matches the rendered bands
    const t = (dist - INNER) / (OUTER - INNER);
    const l = Math.round(35 + t * 50);
    const hex = hslToHex(hue, 80, l);
    onSelect(hex);
  }, [onSelect]);

  const isHighlighted = (hue) => {
    if (!highlightedHues || highlightedHues.length === 0) return true;
    return highlightedHues.some(h => {
      const diff = Math.abs((hue - h + 360) % 360);
      return Math.min(diff, 360 - diff) <= 18;
    });
  };

  return (
    <svg
      ref={svgRef}
      width={SIZE}
      height={SIZE}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      onClick={handleClick}
      style={{ cursor: 'crosshair', filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.5))' }}
    >
      <defs>
        <radialGradient id="centerGlow" cx="50%" cy="50%" r="42%">
          <stop offset="0%" stopColor={selectedHex} stopOpacity="0.3" />
          <stop offset="100%" stopColor={selectedHex} stopOpacity="0" />
        </radialGradient>
      </defs>

      {Array.from({ length: SEGMENTS }).map((_, i) => {
        const startAngle = i * SEG_ANGLE;
        const endAngle = startAngle + SEG_ANGLE + 0.5;
        const midAngle = startAngle + SEG_ANGLE / 2;
        const hue = Math.round(midAngle) % 360;
        const lit = isHighlighted(hue);

        // Two radial bands: outer lighter, inner darker
        const outerHex = hslToHex(hue, 80, 70);
        const innerHex = hslToHex(hue, 90, 35);

        return (
          <g key={i} opacity={lit ? 1 : 0.12} style={{ transition: 'opacity 0.35s ease' }}>
            <path
              d={describeSegment(startAngle, endAngle, OUTER - 1, (OUTER + INNER) / 2)}
              fill={outerHex}
            />
            <path
              d={describeSegment(startAngle, endAngle, (OUTER + INNER) / 2, INNER + 1)}
              fill={innerHex}
            />
          </g>
        );
      })}

      {/* Center fill */}
      <circle cx={CENTER} cy={CENTER} r={INNER} fill="url(#centerGlow)" />
      <circle cx={CENTER} cy={CENTER} r={INNER} fill="#0b1228" opacity="0.9" />

      {/* Selected colour indicator on wheel */}
      {(() => {
        const angle = selH;
        const r = INNER + (OUTER - INNER) * 0.5;
        const pos = polarToCart(angle, r);
        return (
          <circle
            cx={pos.x}
            cy={pos.y}
            r={9}
            fill={selectedHex}
            stroke="#f5f0e8"
            strokeWidth={2}
            style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.4))' }}
          />
        );
      })()}

      {/* Tick marks for 30° intervals (colour primaries) */}
      {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map(a => {
        const inner = polarToCart(a, OUTER - 2);
        const outer = polarToCart(a, OUTER + 8);
        return (
          <line
            key={a}
            x1={inner.x} y1={inner.y}
            x2={outer.x} y2={outer.y}
            stroke="#f5f0e8"
            strokeWidth={1}
            opacity={0.3}
          />
        );
      })}
    </svg>
  );
}
